import React, { useState, useEffect, useRef, useMemo } from 'react';
import { 
  Settings as SettingsIcon, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Download, 
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  AlertCircle,
  MoreHorizontal,
  Database,
  Globe,
  Mail,
  Bell,
  Shield,
  Palette,
  Monitor,
  Save,
  X,
  ChevronDown,
  ChevronRight,
  List,
  Tag,
  Eye,
  EyeOff,
  Hash,
  Layers,
  Play,
  Building
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import TenantOnboardingForm from './TenantOnboardingForm';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import DropdownService from '../../services/dropdownService';
import AutoIncrementService from '../../services/autoIncrementService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';

const Settings = () => {
  const { user } = useAuth();
  const { success, error: showError, toasts, removeToast } = useToast();
  const isSuperAdmin = user?.role?.name === 'super_admin';
  const [activeTab, setActiveTab] = useState('general');
  const [dropdowns, setDropdowns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSystemFilter, setIsSystemFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showValueModal, setShowValueModal] = useState(false);
  const [selectedDropdown, setSelectedDropdown] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    description: '',
    values: [],
    isSystem: false
  });

  // Value form state
  const [valueFormData, setValueFormData] = useState({
    label: '',
    value: '',
    color: '#6b7280',
    order: 1
  });

  // System settings state - Seeded data for all companies
  const [systemSettings, setSystemSettings] = useState({
    // General App Configuration
    testMode: false,
    maintenanceMode: false,
    emailNotifications: true,
    smsNotifications: false,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    defaultCurrency: 'USD',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    language: 'en',
    apiRateLimiting: true,
    logLevel: 'info',
    enableAuditLog: true,
    autoBackup: true,
    backupFrequency: 'daily'
  });

  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  // Auto Increment state
  const [autoIncrements, setAutoIncrements] = useState([]);
  const [loadingAutoIncrement, setLoadingAutoIncrement] = useState(false);
  const [autoIncrementSearch, setAutoIncrementSearch] = useState('');
  const [autoIncrementStrategyFilter, setAutoIncrementStrategyFilter] = useState('');
  const [showAutoIncrementModal, setShowAutoIncrementModal] = useState(false);
  const [showAutoIncrementDeleteModal, setShowAutoIncrementDeleteModal] = useState(false);
  const [selectedAutoIncrement, setSelectedAutoIncrement] = useState(null);
  const [autoIncrementPagination, setAutoIncrementPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // Auto Increment Action Dropdown Component
  const AutoIncrementActionDropdown = ({ item, onGenerate, onEdit, onDelete }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const handleToggle = () => {
      if (!isOpen) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const menuHeight = 120;
        setOpenUpward(spaceBelow < menuHeight);
      }
      setIsOpen(!isOpen);
    };

    const handleGenerate = () => {
      setIsOpen(false);
      onGenerate && onGenerate(item);
    };

    const handleEdit = () => {
      setIsOpen(false);
      onEdit && onEdit(item);
    };

    const handleDelete = () => {
      setIsOpen(false);
      onDelete && onDelete(item);
    };

    return (
      <div className="dropdown" ref={dropdownRef} style={{ position: 'relative', zIndex: 1050 }}>
        <button
          ref={buttonRef}
          className="btn btn-outline-secondary btn-sm"
          type="button"
          onClick={handleToggle}
          style={{
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            color: '#3A8DCC',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '32px',
            height: '32px',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#E8F1FD';
            e.target.style.borderColor = '#3A8DCC';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#ffffff';
            e.target.style.borderColor = '#e5e7eb';
          }}
        >
          <MoreHorizontal size={16} style={{ color: '#3A8DCC' }} />
        </button>
        {isOpen && (
          <ul className="dropdown-menu dropdown-menu-end show" style={{
            position: 'fixed',
            [openUpward ? 'bottom' : 'top']: openUpward ? `${window.innerHeight - buttonRef.current.getBoundingClientRect().top + 8}px` : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            right: `${window.innerWidth - buttonRef.current.getBoundingClientRect().right}px`,
            zIndex: 9999,
            minWidth: '180px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            borderRadius: '0',
            padding: '4px 0',
            backgroundColor: 'white',
            listStyle: 'none',
            margin: 0
          }}>
            <li>
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={handleGenerate}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#374151',
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <Play size={16} style={{ marginRight: '10px', color: '#16a34a', flexShrink: 0 }} />
                Generate Next Number
              </button>
            </li>
            <li>
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={handleEdit}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#374151',
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8fafc';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <Edit size={16} style={{ marginRight: '10px', color: '#6b7280', flexShrink: 0 }} />
                Edit
              </button>
            </li>
            <li style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
              <button
                className="dropdown-item d-flex align-items-center"
                onClick={handleDelete}
                style={{
                  padding: '10px 16px',
                  fontSize: '14px',
                  color: '#dc2626',
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: '100%',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family)',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#fef2f2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                }}
              >
                <Trash2 size={16} style={{ marginRight: '10px', color: '#dc2626', flexShrink: 0 }} />
                Delete
              </button>
            </li>
          </ul>
        )}
      </div>
    );
  };
  const [autoIncrementFormData, setAutoIncrementFormData] = useState({
    moduleName: '',
    moduleCode: '',
    modulePrefix: '',
    strategy: 'flat',
    currentValue: 0,
    digitCount: 4,
    categoryConfigs: [],
    isActive: true,
    description: '',
    yearOption: 'none' // 'none', 'current', 'fy-jan-dec', 'fy-apr-mar'
  });
  const [newCategoryConfig, setNewCategoryConfig] = useState({
    category: '',
    prefix: '',
    currentValue: 0,
    digitCount: 4
  });

  // Default to Tenant Onboarding tab for super_admin when they open Settings
  const hasSetSuperAdminTab = useRef(false);
  useEffect(() => {
    if (isSuperAdmin && !hasSetSuperAdminTab.current) {
      hasSetSuperAdminTab.current = true;
      setActiveTab('tenant-onboarding');
    }
    if (!isSuperAdmin && activeTab === 'tenant-onboarding') {
      setActiveTab('general');
    }
  }, [isSuperAdmin, activeTab]);

  // Load settings on component mount
  useEffect(() => {
    if (activeTab === 'general') {
      loadSettings();
    } else if (activeTab === 'auto-increment') {
      loadAutoIncrements();
    }
  }, [activeTab]);

  // Load settings from API
  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.SETTINGS}`, {
        headers: API_CONFIG.HEADERS
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setSystemSettings({
            testMode: result.data.testMode ?? false,
            maintenanceMode: result.data.maintenanceMode ?? false,
            emailNotifications: result.data.emailNotifications ?? true,
            smsNotifications: result.data.smsNotifications ?? false,
            sessionTimeout: result.data.sessionTimeout ?? 30,
            maxLoginAttempts: result.data.maxLoginAttempts ?? 5,
            defaultCurrency: result.data.defaultCurrency ?? 'USD',
            timezone: result.data.timezone ?? 'UTC',
            dateFormat: result.data.dateFormat ?? 'MM/DD/YYYY',
            language: result.data.language ?? 'en',
            apiRateLimiting: result.data.apiRateLimiting ?? true,
            logLevel: result.data.logLevel ?? 'info',
            enableAuditLog: result.data.enableAuditLog ?? true,
            autoBackup: result.data.autoBackup ?? true,
            backupFrequency: result.data.backupFrequency ?? 'daily'
          });
        }
      } else {
        console.error('Error loading settings:', response.statusText);
        showError('Failed to load settings');
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      showError('Failed to load settings: ' + error.message);
    } finally {
      setLoadingSettings(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.SETTINGS}`, {
        method: 'PUT',
        headers: API_CONFIG.HEADERS,
        body: JSON.stringify(systemSettings)
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          success('Settings saved successfully!');
        } else {
          showError('Error saving settings: ' + (result.message || 'Unknown error'));
        }
      } else {
        const error = await response.json();
        showError('Error saving settings: ' + (error.message || response.statusText));
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showError('Error saving settings: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  // Seed settings for all companies
  const handleSeedSettings = async () => {
    if (!window.confirm('This will seed default settings for all companies. Continue?')) {
      return;
    }

    try {
      const response = await fetch(`${API_CONFIG.ENDPOINTS.SETTINGS}/seed`, {
        method: 'POST',
        headers: API_CONFIG.HEADERS
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          success(`Settings seeded successfully for ${result.data.length} companies!`);
        } else {
          showError('Error seeding settings: ' + (result.message || 'Unknown error'));
        }
      } else {
        const error = await response.json();
        showError('Error seeding settings: ' + (error.message || response.statusText));
      }
    } catch (error) {
      console.error('Error seeding settings:', error);
      showError('Error seeding settings: ' + error.message);
    }
  };

  // Handle input change
  const handleSettingChange = (key, value) => {
    setSystemSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const tabs = useMemo(() => {
    const baseTabs = [
      { id: 'general', label: 'General Settings', icon: <SettingsIcon size={18} /> },
      { id: 'auto-increment', label: 'Auto Increment', icon: <Hash size={18} /> },
      { id: 'appearance', label: 'Appearance', icon: <Palette size={18} /> },
      { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
      { id: 'security', label: 'Security', icon: <Shield size={18} /> },
      { id: 'system', label: 'System', icon: <Monitor size={18} /> }
    ];
    if (isSuperAdmin) {
      return [
        { id: 'tenant-onboarding', label: 'Tenant Onboarding', icon: <Building size={18} /> },
        ...baseTabs
      ];
    }
    return baseTabs;
  }, [isSuperAdmin]);


  // Load dropdowns with current filters
  const loadDropdowns = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.itemsPerPage,
        search: searchTerm,
        isSystem: isSystemFilter,
        sortBy: 'name',
        sortOrder: 'asc'
      };

      const response = await DropdownService.getDropdowns(params);
      setDropdowns(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error loading dropdowns:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    loadDropdowns(1);
  };

  // Handle filter changes
  const handleFilterChange = (filterType, value) => {
    if (filterType === 'isSystem') {
      setIsSystemFilter(value);
    }
    loadDropdowns(1);
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setIsSystemFilter('');
    loadDropdowns(1);
  };

  // Handle create dropdown
  const handleCreateDropdown = async () => {
    try {
      await DropdownService.createDropdown(formData);
      setShowCreateModal(false);
      resetForm();
      loadDropdowns();
      success('Dropdown created successfully!');
    } catch (error) {
      console.error('Error creating dropdown:', error);
      showError(error.message || 'Failed to create dropdown');
    }
  };

  // Handle edit dropdown
  const handleEditDropdown = async () => {
    try {
      await DropdownService.updateDropdown(selectedDropdown.id, formData);
      setShowEditModal(false);
      resetForm();
      setSelectedDropdown(null);
      loadDropdowns();
      success('Dropdown updated successfully!');
    } catch (error) {
      console.error('Error updating dropdown:', error);
      showError(error.message || 'Failed to update dropdown');
    }
  };

  // Handle delete dropdown
  const handleDeleteDropdown = async () => {
    try {
      await DropdownService.deleteDropdown(selectedDropdown.id);
      setShowDeleteModal(false);
      setSelectedDropdown(null);
      loadDropdowns();
      success('Dropdown deleted successfully!');
    } catch (error) {
      console.error('Error deleting dropdown:', error);
      showError(error.message || 'Failed to delete dropdown');
    }
  };

  // Handle add value
  const handleAddValue = async () => {
    try {
      await DropdownService.addDropdownValue(selectedDropdown.id, valueFormData);
      setShowValueModal(false);
      resetValueForm();
      loadDropdowns();
      success('Value added successfully!');
    } catch (error) {
      console.error('Error adding value:', error);
      showError(error.message || 'Failed to add value');
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      label: '',
      description: '',
      values: [],
      isSystem: false
    });
  };

  // Reset value form
  const resetValueForm = () => {
    setValueFormData({
      label: '',
      value: '',
      color: '#6b7280',
      order: 1
    });
  };

  // Open edit modal
  const openEditModal = (dropdown) => {
    setSelectedDropdown(dropdown);
    setFormData({
      name: dropdown.name,
      label: dropdown.label,
      description: dropdown.description,
      values: dropdown.values,
      isSystem: dropdown.isSystem
    });
    setShowEditModal(true);
  };

  // Open delete modal
  const openDeleteModal = (dropdown) => {
    setSelectedDropdown(dropdown);
    setShowDeleteModal(true);
  };

  // Open value modal
  const openValueModal = (dropdown) => {
    setSelectedDropdown(dropdown);
    setValueFormData({
      label: '',
      value: '',
      color: '#6b7280',
      order: dropdown.values.length + 1
    });
    setShowValueModal(true);
  };

  // Get status badge
  const getStatusBadge = (isSystem) => {
    const statusConfig = {
      true: { color: '#f59e0b', icon: Shield, text: 'System' },
      false: { color: '#10b981', icon: Database, text: 'Custom' }
    };

    const config = statusConfig[isSystem.toString()];
    const Icon = config.icon;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '500',
        backgroundColor: `${config.color}20`,
        color: config.color
      }}>
        <Icon size={12} />
        {config.text}
      </span>
    );
  };

  // Table columns for dropdowns
  const dropdownColumns = [
    {
      key: 'name',
      label: 'Dropdown Name',
      sortable: true,
      render: (dropdown) => {
        if (!dropdown) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: dropdown.isSystem ? '#fef3c7' : 'var(--primary-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: dropdown.isSystem ? '#d97706' : 'white',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <List size={20} />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                {dropdown.label}
                {dropdown.isSystem && (
                  <span style={{
                    marginLeft: '8px',
                    padding: '2px 6px',
                    backgroundColor: '#fef3c7',
                    color: '#d97706',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    SYSTEM
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {dropdown.name} • {dropdown.values ? dropdown.values.length : 0} values
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'description',
      label: 'Description',
      render: (dropdown) => {
        if (!dropdown) return null;
        return (
          <span style={{ fontSize: '14px', color: '#6b7280' }}>
            {dropdown.description || 'No description'}
          </span>
        );
      }
    },
    {
      key: 'isSystem',
      label: 'Type',
      render: (dropdown) => {
        if (!dropdown) return null;
        return getStatusBadge(dropdown.isSystem);
      }
    },
    {
      key: 'values',
      label: 'Values',
      render: (dropdown) => {
        if (!dropdown) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Tag size={14} style={{ color: '#6b7280' }} />
            <span style={{ fontSize: '14px' }}>
              {dropdown.values ? dropdown.values.length : 0} values
            </span>
          </div>
        );
      }
    },
    {
      key: 'createdAt',
      label: 'Created',
      sortable: true,
      render: (dropdown) => {
        if (!dropdown || !dropdown.createdAt) return null;
        
        return (
          <span style={{ fontSize: '14px' }}>
            {new Date(dropdown.createdAt).toLocaleDateString()}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (dropdown) => {
        if (!dropdown) return null;
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => openEditModal(dropdown)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Edit Dropdown"
            >
              <Edit size={16} />
            </button>
            
            <button
              onClick={() => openValueModal(dropdown)}
              style={{
                padding: '6px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#6b7280'
              }}
              title="Add Value"
            >
              <Plus size={16} />
            </button>

            {!dropdown.isSystem && (
              <button
                onClick={() => openDeleteModal(dropdown)}
                style={{
                  padding: '6px',
                  backgroundColor: '#fef2f2',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#dc2626'
                }}
                title="Delete Dropdown"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        );
      }
    }
  ];

  // Auto Increment Functions
  const loadAutoIncrements = async (page = 1) => {
    setLoadingAutoIncrement(true);
    try {
      const params = {
        page,
        limit: autoIncrementPagination.itemsPerPage,
        search: autoIncrementSearch,
        strategy: autoIncrementStrategyFilter,
        sortBy: 'moduleName',
        sortOrder: 'asc'
      };

      const response = await AutoIncrementService.getAutoIncrements(params);
      setAutoIncrements(response.data || []);
      // Map pagination to match DataTable expectations
      setAutoIncrementPagination({
        ...response.pagination,
        total: response.pagination?.totalItems || response.pagination?.total || 0
      });
    } catch (error) {
      console.error('Error loading auto increments:', error);
      showError(error.message || 'Failed to load auto increments');
    } finally {
      setLoadingAutoIncrement(false);
    }
  };

  const handleAutoIncrementSearch = (value) => {
    setAutoIncrementSearch(value);
    loadAutoIncrements(1);
  };

  const handleAutoIncrementFilterChange = (filterType, value) => {
    if (filterType === 'strategy') {
      setAutoIncrementStrategyFilter(value);
    }
    loadAutoIncrements(1);
  };

  const clearAutoIncrementFilters = () => {
    setAutoIncrementSearch('');
    setAutoIncrementStrategyFilter('');
    loadAutoIncrements(1);
  };

  const resetAutoIncrementForm = () => {
    setAutoIncrementFormData({
      moduleName: '',
      moduleCode: '',
      modulePrefix: '',
      strategy: 'flat',
      currentValue: 0,
      digitCount: 4,
      categoryConfigs: [],
      isActive: true,
      description: '',
      yearOption: 'none'
    });
    setNewCategoryConfig({
      category: '',
      prefix: '',
      currentValue: 0,
      digitCount: 4
    });
  };

  const openAutoIncrementModal = (autoIncrement = null) => {
    if (autoIncrement) {
      setSelectedAutoIncrement(autoIncrement);
      setAutoIncrementFormData({
        moduleName: autoIncrement.moduleName,
        moduleCode: autoIncrement.moduleCode,
        modulePrefix: autoIncrement.modulePrefix,
        strategy: autoIncrement.strategy,
        currentValue: autoIncrement.currentValue,
        digitCount: autoIncrement.digitCount,
        categoryConfigs: autoIncrement.categoryConfigs || [],
        isActive: autoIncrement.isActive,
        description: autoIncrement.description || '',
        yearOption: autoIncrement.yearOption || 'none'
      });
    } else {
      resetAutoIncrementForm();
      setSelectedAutoIncrement(null);
    }
    setShowAutoIncrementModal(true);
  };

  // Generate preview of auto increment number
  const generatePreview = () => {
    const paddedNumber = String(autoIncrementFormData.currentValue + 1).padStart(autoIncrementFormData.digitCount, '0');
    
    // If no prefix, return just the number
    if (!autoIncrementFormData.modulePrefix) {
      return paddedNumber;
    }

    // Normalize prefix - remove trailing slash if present
    const normalizedPrefix = autoIncrementFormData.modulePrefix.replace(/\/+$/, '');
    
    let yearPart = '';
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // 1-12

    switch (autoIncrementFormData.yearOption) {
      case 'current':
        // Include Current Year: PRJ/2025/0001
        yearPart = `/${currentYear}/`;
        break;
      case 'fy-jan-dec':
        // FY Jan to Dec: PRJ/2025/0001 (if current month is Jan-Dec of 2025)
        yearPart = `/${currentYear}/`;
        break;
      case 'fy-apr-mar':
        // FY Apr to Mar: PRJ/2024-25/0001 (if current month is Apr 2024 - Mar 2025)
        const fyStartYear = currentMonth >= 4 ? currentYear : currentYear - 1;
        const fyEndYear = currentMonth >= 4 ? currentYear + 1 : currentYear;
        yearPart = `/${fyStartYear}-${String(fyEndYear).slice(-2)}/`;
        break;
      default:
        // No year: PRJ/0001 (add / separator)
        yearPart = '/';
    }

    // Format: prefix/yearPart/number
    // If year is included, yearPart is /year/
    // If no year, yearPart is just /
    return `${normalizedPrefix}${yearPart}${paddedNumber}`;
  };

  const handleCreateAutoIncrement = async () => {
    try {
      await AutoIncrementService.createAutoIncrement(autoIncrementFormData);
      setShowAutoIncrementModal(false);
      resetAutoIncrementForm();
      loadAutoIncrements();
      success('Auto increment configuration created successfully!');
    } catch (error) {
      console.error('Error creating auto increment:', error);
      showError(error.message || 'Failed to create auto increment configuration');
    }
  };

  const handleUpdateAutoIncrement = async () => {
    try {
      await AutoIncrementService.updateAutoIncrement(selectedAutoIncrement._id, autoIncrementFormData);
      setShowAutoIncrementModal(false);
      resetAutoIncrementForm();
      setSelectedAutoIncrement(null);
      loadAutoIncrements();
      success('Auto increment configuration updated successfully!');
    } catch (error) {
      console.error('Error updating auto increment:', error);
      showError(error.message || 'Failed to update auto increment configuration');
    }
  };

  const handleDeleteAutoIncrement = async () => {
    try {
      await AutoIncrementService.deleteAutoIncrement(selectedAutoIncrement._id);
      setShowAutoIncrementDeleteModal(false);
      setSelectedAutoIncrement(null);
      loadAutoIncrements();
      success('Auto increment configuration deleted successfully!');
    } catch (error) {
      console.error('Error deleting auto increment:', error);
      showError(error.message || 'Failed to delete auto increment configuration');
    }
  };

  const openDeleteAutoIncrementModal = (autoIncrement) => {
    setSelectedAutoIncrement(autoIncrement);
    setShowAutoIncrementDeleteModal(true);
  };

  const handleGenerateNextNumber = async (autoIncrement, category = null) => {
    try {
      const response = await AutoIncrementService.generateNextNumber(autoIncrement._id, category);
      success(`Next number generated: ${response.data.nextNumber}`);
      loadAutoIncrements();
    } catch (error) {
      console.error('Error generating next number:', error);
      showError(error.message || 'Failed to generate next number');
    }
  };

  const addCategoryConfig = () => {
    if (!newCategoryConfig.category || !newCategoryConfig.prefix) {
      showError('Category and prefix are required');
      return;
    }

    const updatedConfigs = [...autoIncrementFormData.categoryConfigs, { ...newCategoryConfig }];
    setAutoIncrementFormData({
      ...autoIncrementFormData,
      categoryConfigs: updatedConfigs
    });
    setNewCategoryConfig({
      category: '',
      prefix: '',
      currentValue: 0,
      digitCount: 4
    });
  };

  const removeCategoryConfig = (index) => {
    const updatedConfigs = autoIncrementFormData.categoryConfigs.filter((_, i) => i !== index);
    setAutoIncrementFormData({
      ...autoIncrementFormData,
      categoryConfigs: updatedConfigs
    });
  };

  // Get header actions based on active tab
  const getHeaderActions = () => {
    if (activeTab === 'auto-increment') {
      return [
        {
          label: '',
          icon: <RefreshCw size={18} className={loadingAutoIncrement ? 'spinning' : ''} />,
          onClick: () => loadAutoIncrements(),
          variant: 'outline',
          tooltip: 'Refresh auto increments',
          disabled: loadingAutoIncrement
        },
        {
          label: 'Add Auto Increment',
          icon: <Plus size={18} />,
          onClick: () => openAutoIncrementModal(),
          variant: 'primary',
          tooltip: 'Create a new auto increment configuration'
        }
      ];
    }
    // Add actions for other tabs here if needed
    return [];
  };

  // Auto Increment Table Columns
  const autoIncrementColumns = [
    {
      key: 'moduleName',
      label: 'Module Name',
      sortable: true,
      align: 'left',
      render: (value, row) => {
        const item = row; // row is the full object
        if (!item) return null;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: '#4678be',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '16px',
              fontWeight: '600'
            }}>
              <Hash size={20} />
            </div>
            <div>
              <div style={{ fontWeight: '600', color: '#1f2937' }}>
                {item.moduleName}
              </div>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {item.moduleCode} • {item.strategy === 'category' ? 'Category-based' : 'Flat'}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'modulePrefix',
      label: 'Prefix',
      align: 'center',
      render: (value, row) => {
        if (!row) return null;
        return (
          <span style={{ fontSize: '14px', fontFamily: 'monospace', color: '#4678be' }}>
            {row.modulePrefix || ''}
          </span>
        );
      }
    },
    {
      key: 'strategy',
      label: 'Strategy',
      align: 'center',
      render: (value, row) => {
        if (!row) return null;
        const isCategory = row.strategy === 'category';
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: isCategory ? '#dbeafe' : '#f3f4f6',
            color: isCategory ? '#1e40af' : '#6b7280'
          }}>
            {isCategory ? 'Category-based' : 'Flat'}
          </span>
        );
      }
    },
    {
      key: 'currentValue',
      label: 'Current Value',
      align: 'center',
      render: (value, row) => {
        if (!row) return null;
        if (row.strategy === 'category') {
          return (
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {row.categoryConfigs?.length || 0} categories
            </div>
          );
        }
        return (
          <span style={{ fontSize: '14px', fontWeight: '500' }}>
            {row.currentValue}
          </span>
        );
      }
    },
    {
      key: 'digitCount',
      label: 'Digits',
      align: 'center',
      render: (value, row) => {
        if (!row) return null;
        return (
          <span style={{ fontSize: '14px' }}>
            {row.digitCount}
          </span>
        );
      }
    },
    {
      key: 'isActive',
      label: 'Status',
      align: 'center',
      render: (value, row) => {
        if (!row) return null;
        return (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '4px 8px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor: row.isActive ? '#d1fae5' : '#fee2e2',
            color: row.isActive ? '#065f46' : '#991b1b'
          }}>
            {row.isActive ? 'Active' : 'Inactive'}
          </span>
        );
      }
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'center',
      render: (value, row) => {
        const item = row; // row is the full object
        if (!item) return null;
        return (
          <AutoIncrementActionDropdown
            item={item}
            onGenerate={handleGenerateNextNumber}
            onEdit={openAutoIncrementModal}
            onDelete={openDeleteAutoIncrementModal}
          />
        );
      }
    }
  ];

  return (
    <>
      <style>{`
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
      <PageBreadcrumb />
      <PageHeader 
        title="System Settings"
        description="Configure system settings, dropdown values, and application preferences"
        actions={getHeaderActions()}
      />
      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>

      {/* Tabbed Navigation */}
      <div className="project-tabs" style={{ marginBottom: '0', width: '100%', maxWidth: '100%' }}>
        <div className="project-tabs-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`project-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <div className="project-tab-icon">
                {tab.icon}
              </div>
              <div className="project-tab-content">
                <div className="project-tab-label">{tab.label}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Tenant Onboarding Tab (super_admin only) */}
      {activeTab === 'tenant-onboarding' && isSuperAdmin && (
        <div style={{ width: '100%', maxWidth: '100%' }}>
          <TenantOnboardingForm />
        </div>
      )}

      {/* General Settings Tab */}
      {activeTab === 'general' && (
        <div className="project-form-card" style={{ borderTop: 'none', width: '100%', maxWidth: '100%' }}>
          {loadingSettings ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              padding: '40px' 
            }}>
              <div className="loading-spinner"></div>
              <span style={{ marginLeft: '12px', color: '#6b7280' }}>Loading settings...</span>
            </div>
          ) : (
          <div className="project-form-section">
            <div className="section-header">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h3 className="section-title">General Settings</h3>
                  <p className="section-description">Configure general application settings and preferences</p>
                </div>
                <button
                  type="button"
                  className="btn-modern btn-modern-outline"
                  onClick={handleSeedSettings}
                  style={{ marginTop: '0' }}
                >
                  <Database size={16} />
                  Seed All Companies
                </button>
              </div>
            </div>

            <div className="form-grid-2">
              {/* Test Mode */}
              <div className="form-group">
                <label className="form-label">Test Mode</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.testMode}
                      onChange={(e) => handleSettingChange('testMode', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable test mode for development and testing</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  When enabled, the system will operate in test mode with relaxed validation
                </small>
              </div>

              {/* Maintenance Mode */}
              <div className="form-group">
                <label className="form-label">Maintenance Mode</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.maintenanceMode}
                      onChange={(e) => handleSettingChange('maintenanceMode', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable maintenance mode</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  When enabled, only administrators can access the system
                </small>
              </div>

              {/* Email Notifications */}
              <div className="form-group">
                <label className="form-label">Email Notifications</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable email notifications</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Send email notifications for important system events
                </small>
              </div>

              {/* SMS Notifications */}
              <div className="form-group">
                <label className="form-label">SMS Notifications</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.smsNotifications}
                      onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable SMS notifications</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Send SMS notifications for critical alerts
                </small>
              </div>

              {/* Session Timeout */}
              <div className="form-group">
                <label className="form-label">Session Timeout (minutes)</label>
                <input
                  type="number"
                  className="form-control"
                  value={systemSettings.sessionTimeout}
                  onChange={(e) => handleSettingChange('sessionTimeout', parseInt(e.target.value) || 30)}
                  min="5"
                  max="480"
                />
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Automatically log out users after inactivity period
                </small>
              </div>

              {/* Max Login Attempts */}
              <div className="form-group">
                <label className="form-label">Max Login Attempts</label>
                <input
                  type="number"
                  className="form-control"
                  value={systemSettings.maxLoginAttempts}
                  onChange={(e) => handleSettingChange('maxLoginAttempts', parseInt(e.target.value) || 5)}
                  min="3"
                  max="10"
                />
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Maximum failed login attempts before account lockout
                </small>
              </div>

              {/* Default Currency */}
              <div className="form-group">
                <label className="form-label">Default Currency</label>
                <select
                  className="form-select"
                  value={systemSettings.defaultCurrency}
                  onChange={(e) => handleSettingChange('defaultCurrency', e.target.value)}
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="INR">INR - Indian Rupee</option>
                  <option value="JPY">JPY - Japanese Yen</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Default currency for financial transactions
                </small>
              </div>

              {/* Timezone */}
              <div className="form-group">
                <label className="form-label">Timezone</label>
                <select
                  className="form-select"
                  value={systemSettings.timezone}
                  onChange={(e) => handleSettingChange('timezone', e.target.value)}
                >
                  <option value="UTC">UTC - Coordinated Universal Time</option>
                  <option value="America/New_York">America/New_York - Eastern Time</option>
                  <option value="America/Chicago">America/Chicago - Central Time</option>
                  <option value="America/Denver">America/Denver - Mountain Time</option>
                  <option value="America/Los_Angeles">America/Los_Angeles - Pacific Time</option>
                  <option value="Europe/London">Europe/London - GMT</option>
                  <option value="Europe/Paris">Europe/Paris - CET</option>
                  <option value="Asia/Dubai">Asia/Dubai - GST</option>
                  <option value="Asia/Kolkata">Asia/Kolkata - IST</option>
                  <option value="Asia/Tokyo">Asia/Tokyo - JST</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  System timezone for date and time display
                </small>
              </div>

              {/* Date Format */}
              <div className="form-group">
                <label className="form-label">Date Format</label>
                <select
                  className="form-select"
                  value={systemSettings.dateFormat}
                  onChange={(e) => handleSettingChange('dateFormat', e.target.value)}
                >
                  <option value="MM/DD/YYYY">MM/DD/YYYY (US Format)</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY (European Format)</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD (ISO Format)</option>
                  <option value="DD-MM-YYYY">DD-MM-YYYY (Alternative Format)</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Default date format for display throughout the application
                </small>
              </div>

              {/* Language */}
              <div className="form-group">
                <label className="form-label">Language</label>
                <select
                  className="form-select"
                  value={systemSettings.language}
                  onChange={(e) => handleSettingChange('language', e.target.value)}
                >
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="zh">Chinese</option>
                  <option value="ja">Japanese</option>
                  <option value="ar">Arabic</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Default language for the application interface
                </small>
              </div>

              {/* API Rate Limiting */}
              <div className="form-group">
                <label className="form-label">API Rate Limiting</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.apiRateLimiting}
                      onChange={(e) => handleSettingChange('apiRateLimiting', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable API rate limiting</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Limit API requests to prevent abuse and ensure system stability
                </small>
              </div>

              {/* Log Level */}
              <div className="form-group">
                <label className="form-label">Log Level</label>
                <select
                  className="form-select"
                  value={systemSettings.logLevel}
                  onChange={(e) => handleSettingChange('logLevel', e.target.value)}
                >
                  <option value="error">Error - Only errors</option>
                  <option value="warn">Warn - Warnings and errors</option>
                  <option value="info">Info - Informational messages</option>
                  <option value="debug">Debug - Detailed debugging information</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Level of detail for system logging
                </small>
              </div>

              {/* Enable Audit Log */}
              <div className="form-group">
                <label className="form-label">Audit Logging</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.enableAuditLog}
                      onChange={(e) => handleSettingChange('enableAuditLog', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable audit logging</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Track all user actions and system changes for compliance
                </small>
              </div>

              {/* Auto Backup */}
              <div className="form-group">
                <label className="form-label">Auto Backup</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '8px', 
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151'
                  }}>
                    <input
                      type="checkbox"
                      checked={systemSettings.autoBackup}
                      onChange={(e) => handleSettingChange('autoBackup', e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Enable automatic backups</span>
                  </label>
                </div>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Automatically backup system data on a schedule
                </small>
              </div>

              {/* Backup Frequency */}
              <div className="form-group">
                <label className="form-label">Backup Frequency</label>
                <select
                  className="form-select"
                  value={systemSettings.backupFrequency}
                  onChange={(e) => handleSettingChange('backupFrequency', e.target.value)}
                  disabled={!systemSettings.autoBackup}
                >
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                  Frequency of automatic backups
                </small>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '12px', 
              marginTop: '32px',
              paddingTop: '24px',
              borderTop: '1px solid #e2e8f0'
            }}>
              <button
                type="button"
                className="btn-modern btn-modern-outline"
                onClick={() => loadSettings()}
                disabled={loadingSettings}
              >
                <RefreshCw size={16} className={loadingSettings ? 'spinning' : ''} />
                Reset
              </button>
              <button
                type="button"
                className="btn-modern btn-modern-primary"
                onClick={handleSaveSettings}
                disabled={savingSettings}
              >
                <Save size={16} />
                {savingSettings ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
          )}
        </div>
      )}

      {/* Auto Increment Tab */}
      {activeTab === 'auto-increment' && (
        <div style={{ width: '100%', maxWidth: '100%' }}>
          {/* Data Table */}
          <DataTable
            data={autoIncrements}
            columns={autoIncrementColumns}
            title="Auto Increments"
            loading={loadingAutoIncrement}
            pagination={autoIncrementPagination}
            onPageChange={loadAutoIncrements}
            onSearchChange={handleAutoIncrementSearch}
            searchable={true}
            searchPlaceholder="Search auto increments..."
            sortable={true}
            exportable={true}
            showActions={false}
          />
        </div>
      )}

      {/* Other Settings Tabs */}
      {activeTab !== 'general' && activeTab !== 'auto-increment' && activeTab !== 'tenant-onboarding' && (
        <div className="project-form-card" style={{ width: '100%', maxWidth: '100%' }}>
          <div style={{
            textAlign: 'center',
            padding: '40px 20px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '0',
              backgroundColor: '#f3f4f6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px auto'
            }}>
              <SettingsIcon size={32} style={{ color: '#6b7280' }} />
            </div>
            <h3 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#1f2937',
              margin: '0 0 12px 0',
              fontFamily: 'var(--font-family)'
            }}>
              {tabs.find(tab => tab.id === activeTab)?.label}
            </h3>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: '0 0 24px 0',
              maxWidth: '500px',
              margin: '0 auto 24px auto',
              fontFamily: 'var(--font-family)'
            }}>
              {activeTab === 'appearance' && 'Customize the look and feel of the application.'}
              {activeTab === 'notifications' && 'Manage notification preferences and settings.'}
              {activeTab === 'security' && 'Configure security settings and policies.'}
              {activeTab === 'system' && 'System-level configuration and maintenance settings.'}
            </p>
            <button 
              className="btn-modern btn-modern-primary"
            >
              <SettingsIcon size={20} />
              Configure Settings
            </button>
          </div>
        </div>
      )}

      {/* Auto Increment Create/Edit Modal - Slide-out from Right */}
      {showAutoIncrementModal && (
        <>
          {/* Backdrop */}
          <div 
            style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1040,
              animation: 'fadeIn 0.3s ease-out'
            }}
            onClick={() => {
              setShowAutoIncrementModal(false);
              resetAutoIncrementForm();
              setSelectedAutoIncrement(null);
            }}
          />
          {/* Slide-out Panel */}
          <div 
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxWidth: '600px',
              backgroundColor: 'white',
              boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)',
              zIndex: 1050,
          display: 'flex',
              flexDirection: 'column',
              animation: 'slideInFromRight 0.3s ease-out',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Primary Color */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
          alignItems: 'center',
              padding: '16px 20px',
              backgroundColor: '#4678be',
              color: 'white',
              flexShrink: 0
            }}>
              <div>
            <h3 style={{
                  fontSize: '18px',
              fontWeight: '600',
                  color: 'white',
                  margin: 0,
                  marginBottom: '2px'
            }}>
                  {selectedAutoIncrement ? 'Edit Auto Increment' : 'Create Auto Increment'}
            </h3>
                <p style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.85)',
                  margin: 0
                }}>
                  {selectedAutoIncrement ? 'Update auto increment configuration' : 'Define auto increment strategy'}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAutoIncrementModal(false);
                  resetAutoIncrementForm();
                  setSelectedAutoIncrement(null);
                }}
                style={{
                  padding: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Scrollable Content */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '20px 24px'
            }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Row 1: Module Name, Preview */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Module Name *
              </label>
              <input
                type="text"
                    className="form-control"
                    value={autoIncrementFormData.moduleName}
                    onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, moduleName: e.target.value })}
                    placeholder="e.g., Project Management"
                  />
                </div>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Preview
                  </label>
                  <div className="form-control" style={{
                    border: '2px solid #4678be',
                    backgroundColor: '#f0f7ff',
                    fontWeight: '600',
                    color: '#4678be',
                    fontFamily: 'monospace',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {generatePreview()}
                  </div>
                </div>
            </div>

              {/* Row 2: Module Code, Prefix */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Module Code *
              </label>
              <input
                type="text"
                    className="form-control"
                    value={autoIncrementFormData.moduleCode}
                    onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, moduleCode: e.target.value.toUpperCase() })}
                    placeholder="e.g., PRJ"
                    style={{ textTransform: 'uppercase' }}
              />
            </div>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Module Prefix *
              </label>
                  <input
                    type="text"
                    className="form-control"
                    value={autoIncrementFormData.modulePrefix}
                    onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, modulePrefix: e.target.value })}
                    placeholder="e.g., PRJ/"
                  />
                </div>
            </div>

              {/* Row 3: Include Year, Strategy */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Include Year After Prefix
                  </label>
                  <select
                    className="form-control form-select"
                    value={autoIncrementFormData.yearOption}
                    onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, yearOption: e.target.value })}
                  >
                    <option value="none">None - No year included</option>
                    <option value="current">Include Current Year</option>
                    <option value="fy-jan-dec">FY Jan to Dec</option>
                    <option value="fy-apr-mar">FY Apr to Mar</option>
                  </select>
                </div>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Strategy *
                  </label>
                  <select
                    className="form-control form-select"
                    value={autoIncrementFormData.strategy}
                    onChange={(e) => {
                      const strategy = e.target.value;
                      setAutoIncrementFormData({
                        ...autoIncrementFormData,
                        strategy,
                        categoryConfigs: strategy === 'flat' ? [] : autoIncrementFormData.categoryConfigs
                      });
                    }}
                  >
                    <option value="flat">Flat - Single prefix for all</option>
                    <option value="category">Category-based - Different prefix per category</option>
                  </select>
            </div>
          </div>

              {/* Row 4: Current Value, Digit Count */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Current Value
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={autoIncrementFormData.currentValue}
                    onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, currentValue: parseInt(e.target.value) || 0 })}
                    min="0"
                  />
        </div>
                <div>
                  <label className="form-label" style={{ marginBottom: '6px' }}>
                    Digit Count
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={autoIncrementFormData.digitCount}
                    onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, digitCount: parseInt(e.target.value) || 4 })}
                    min="1"
                    max="10"
                  />
                  <small style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', display: 'block' }}>
                    Number of digits (e.g., 4 = 0001, 0002...)
                  </small>
                </div>
              </div>

              {/* Flat Strategy - Description and Active Status Row */}
              {autoIncrementFormData.strategy === 'flat' && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label className="form-label" style={{ marginBottom: '6px' }}>
                      Description
              </label>
              <input
                type="text"
                      className="form-control"
                      value={autoIncrementFormData.description}
                      onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, description: e.target.value })}
                      placeholder="Optional description..."
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '8px', 
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#374151',
                  padding: '10px 12px',
                      width: '100%',
                  border: '1px solid #d1d5db',
                      backgroundColor: 'white'
                    }}>
                      <input
                        type="checkbox"
                        checked={autoIncrementFormData.isActive}
                        onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, isActive: e.target.checked })}
                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                      />
                      <span>Active (Enable this auto increment configuration)</span>
                    </label>
            </div>
                </div>
              )}

              {/* Category Strategy Fields */}
              {autoIncrementFormData.strategy === 'category' && (
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                    Category Configurations
              </label>
                  
                    {/* Add Category Form */}
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: '2fr 1.5fr 1fr 1fr auto', 
                      gap: '8px',
                      marginBottom: '12px',
                      padding: '12px',
                      backgroundColor: '#f9fafb'
                    }}>
              <input
                type="text"
                        className="form-control"
                        placeholder="Category name"
                        value={newCategoryConfig.category}
                        onChange={(e) => setNewCategoryConfig({ ...newCategoryConfig, category: e.target.value })}
                      />
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Prefix (e.g., PROJ/)"
                        value={newCategoryConfig.prefix}
                        onChange={(e) => setNewCategoryConfig({ ...newCategoryConfig, prefix: e.target.value })}
                      />
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Start value"
                        value={newCategoryConfig.currentValue}
                        onChange={(e) => setNewCategoryConfig({ ...newCategoryConfig, currentValue: parseInt(e.target.value) || 0 })}
                        min="0"
                      />
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Digits"
                        value={newCategoryConfig.digitCount}
                        onChange={(e) => setNewCategoryConfig({ ...newCategoryConfig, digitCount: parseInt(e.target.value) || 4 })}
                        min="1"
                        max="10"
                      />
                      <button
                        type="button"
                        onClick={addCategoryConfig}
                        className="btn-modern btn-modern-primary"
                        style={{ padding: '8px 16px', whiteSpace: 'nowrap' }}
                      >
                        <Plus size={16} />
                        Add
                      </button>
                    </div>

                    {/* Category Configs List */}
                    {autoIncrementFormData.categoryConfigs.length > 0 && (
                      <div style={{ border: '1px solid #e2e8f0', overflow: 'hidden', marginBottom: '12px' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                          <thead style={{ backgroundColor: '#f9fafb' }}>
                            <tr>
                              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e2e8f0' }}>Category</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e2e8f0' }}>Prefix</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e2e8f0' }}>Value</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontSize: '11px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e2e8f0' }}>Digits</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontSize: '11px', fontWeight: '600', color: '#6b7280', borderBottom: '1px solid #e2e8f0' }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {autoIncrementFormData.categoryConfigs.map((config, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '8px', fontSize: '13px', color: '#1f2937' }}>{config.category}</td>
                                <td style={{ padding: '8px', fontSize: '13px', color: '#1f2937' }}>{config.prefix}</td>
                                <td style={{ padding: '8px', fontSize: '13px', color: '#1f2937' }}>{config.currentValue}</td>
                                <td style={{ padding: '8px', fontSize: '13px', color: '#1f2937' }}>{config.digitCount}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <button
                                    type="button"
                                    onClick={() => removeCategoryConfig(index)}
                style={{
                                      padding: '4px',
                                      backgroundColor: '#fef2f2',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: '#dc2626'
                                    }}
                                    title="Remove"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
            </div>
                    )}
                  
                  {/* Description and Active Status for Category */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                    <div>
                      <label className="form-label" style={{ marginBottom: '6px' }}>
                        Description
              </label>
              <input
                        type="text"
                        className="form-control"
                        value={autoIncrementFormData.description}
                        onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, description: e.target.value })}
                        placeholder="Optional description..."
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <label style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#374151',
                        padding: '10px 12px',
                  width: '100%',
                  border: '1px solid #d1d5db',
                        backgroundColor: 'white'
                      }}>
                        <input
                          type="checkbox"
                          checked={autoIncrementFormData.isActive}
                          onChange={(e) => setAutoIncrementFormData({ ...autoIncrementFormData, isActive: e.target.checked })}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span>Active (Enable this auto increment configuration)</span>
                      </label>
                    </div>
                  </div>
                </div>
              )}
            </div>
            </div>

            {/* Footer with Action Buttons */}
            <div style={{
              padding: '16px 20px',
              borderTop: '1px solid #e2e8f0',
              backgroundColor: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              flexShrink: 0
            }}>
              <button
                onClick={() => {
                  setShowAutoIncrementModal(false);
                  resetAutoIncrementForm();
                  setSelectedAutoIncrement(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'white',
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={selectedAutoIncrement ? handleUpdateAutoIncrement : handleCreateAutoIncrement}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4678be',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  opacity: (!autoIncrementFormData.moduleName || !autoIncrementFormData.moduleCode || !autoIncrementFormData.modulePrefix || (autoIncrementFormData.strategy === 'category' && autoIncrementFormData.categoryConfigs.length === 0)) ? 0.5 : 1,
                  pointerEvents: (!autoIncrementFormData.moduleName || !autoIncrementFormData.moduleCode || !autoIncrementFormData.modulePrefix || (autoIncrementFormData.strategy === 'category' && autoIncrementFormData.categoryConfigs.length === 0)) ? 'none' : 'auto'
                }}
                disabled={!autoIncrementFormData.moduleName || !autoIncrementFormData.moduleCode || !autoIncrementFormData.modulePrefix || (autoIncrementFormData.strategy === 'category' && autoIncrementFormData.categoryConfigs.length === 0)}
              >
                <Save size={16} />
                {selectedAutoIncrement ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showAutoIncrementDeleteModal}
        onClose={() => {
          setShowAutoIncrementDeleteModal(false);
          setSelectedAutoIncrement(null);
        }}
        onConfirm={handleDeleteAutoIncrement}
        title="Delete Auto Increment"
        message={`Are you sure you want to delete the auto increment configuration "${selectedAutoIncrement?.moduleName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default Settings;